/**
 * AI Scribe Recording Utilities
 *
 * Formatting and display helpers for recording data.
 */

import type { RecordingStatus } from './types';

// ─── Duration Formatting ────────────────────────────────────────────

/**
 * Format duration in seconds to a human-readable string.
 * @example formatDuration(2520) → "42 min"
 * @example formatDuration(4500) → "1h 15min"
 * @example formatDuration(45) → "< 1 min"
 */
export function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '--';
  if (seconds < 60) return '< 1 min';

  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }
  return `${mins} min`;
}

/**
 * Format elapsed seconds as HH:MM:SS for the recording timer.
 * @example formatTimer(2520) → "00:42:00"
 */
export function formatTimer(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

// ─── Date Formatting ────────────────────────────────────────────────

/**
 * Format an ISO date string to "14 Mar 2026" format.
 */
export function formatRecordingDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

// ─── Status Config ──────────────────────────────────────────────────

interface StatusConfig {
  label: string;
  dotClass: string;
  badgeClass: string;
}

const STATUS_CONFIGS: Record<RecordingStatus, StatusConfig> = {
  draft: {
    label: 'Draft',
    dotClass: 'bg-muted-foreground',
    badgeClass: 'bg-surface-subtle text-foreground-secondary ring-border'
  },
  started: {
    label: 'Recording',
    dotClass: 'bg-info',
    badgeClass: 'bg-info-subtle text-info-subtle-foreground ring-info/20'
  },
  ready: {
    label: 'Ready for review',
    dotClass: 'bg-success',
    badgeClass:
      'bg-success-subtle text-success-subtle-foreground ring-success/20'
  },
  uploading: {
    label: 'Uploading',
    dotClass: 'bg-info',
    badgeClass: 'bg-info-subtle text-info-subtle-foreground ring-info/20'
  },
  processing: {
    label: 'Processing',
    dotClass: 'bg-info',
    badgeClass: 'bg-info-subtle text-info-subtle-foreground ring-info/20'
  },
  imported: {
    label: 'Imported',
    dotClass: 'bg-muted-foreground',
    badgeClass: 'bg-muted text-foreground-secondary ring-border'
  },
  dismissed: {
    label: 'Dismissed',
    dotClass: 'bg-border-strong',
    badgeClass: 'bg-surface-subtle text-muted-foreground ring-border'
  },
  failed: {
    label: 'Processing failed',
    dotClass: 'bg-error',
    badgeClass: 'bg-error-subtle text-error-subtle-foreground ring-error/20'
  }
};

/**
 * Get display configuration for a recording status.
 */
export function getStatusConfig(status: RecordingStatus): StatusConfig {
  return STATUS_CONFIGS[status];
}
